import { Input } from "@/components/ui/input";
import { deletePushbulletDevice, listPushbulletDevices, upsertPushbulletDevice, type PushbulletDevice } from "@/lib/pushbullet";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
interface PushbulletDeviceSelectorProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  connectorId?: string;
  onConnectorSelect?: (connectorId: string) => void;
}
function deviceLabel(d: PushbulletDevice): string {
  return d.nickname?.trim() || d.type || d.deviceIden.slice(0, 10);
}
export default function PushbulletDeviceSelector({
  label,
  description,
  value,
  onChange,
  required,
  connectorId,
  onConnectorSelect
}: PushbulletDeviceSelectorProps) {
  const [devices, setDevices] = useState<PushbulletDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [addMode, setAddMode] = useState(false);
  const [newDeviceIden, setNewDeviceIden] = useState("");
  const [newNickname, setNewNickname] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const fetchDevices = useCallback(() => {
    setLoading(true);
    listPushbulletDevices(connectorId, 50, 0).then(result => setDevices(result.items)).catch(err => {
      console.error("Failed to load Pushbullet devices", err);
      setDevices([]);
    }).finally(() => setLoading(false));
  }, [connectorId]);
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setAddMode(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);
  const filtered = devices.filter(d => {
    if (!query) return true;
    const q = query.toLowerCase();
    return d.deviceIden.toLowerCase().includes(q) || (d.nickname || "").toLowerCase().includes(q) || (d.type || "").toLowerCase().includes(q);
  });
  const selected = devices.find(d_0 => d_0.deviceIden === value);
  async function handleSaveDevice(e_0: React.FormEvent) {
    e_0.preventDefault();
    setSaveError(null);
    if (!connectorId) {
      setSaveError("Select a Pushbullet connector first.");
      return;
    }
    if (!newDeviceIden.trim()) {
      setSaveError("Device identifier is required.");
      return;
    }
    setSaving(true);
    try {
      const result_0 = await upsertPushbulletDevice({
        connectorId,
        deviceIden: newDeviceIden.trim(),
        nickname: newNickname.trim() || undefined
      });
      setDevices(prev => [result_0.device, ...prev.filter(d_1 => d_1.id !== result_0.device.id)]);
      onChange(result_0.device.deviceIden);
      onConnectorSelect?.(result_0.device.connectorId);
      setAddMode(false);
      setNewDeviceIden("");
      setNewNickname("");
      setIsOpen(false);
    } catch (err_0) {
      setSaveError(err_0 instanceof Error ? err_0.message : "Failed to add device");
    } finally {
      setSaving(false);
    }
  }
  async function handleRemove(id: string) {
    try {
      await deletePushbulletDevice(id);
      setDevices(prev_0 => prev_0.filter(d_2 => d_2.id !== id));
    } catch (err_1) {
      // toast layer not threaded here; log so operators can debug
      console.error("Failed to delete Pushbullet device", err_1);
    }
  }
  return <div className="w-full" ref={containerRef}>
      {label && <label className="field-label mb-1 block">
          {label}
          {!required && <span className="text-ds-text-muted text-xs"> (leave empty = all devices)</span>}
        </label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled mb-1 text-xs">{description}</p>}
      <p className="text-ds-text-muted mb-1 text-xs">
        Devices are imported when the connector is created. Added a new device since? Use{" "}
        <span className="font-medium">+ Add a device by identifier</span> below.
      </p>

      <button type="button" onClick={() => setIsOpen(o => !o)} className={cn("border-ds-border-default bg-ds-surface-primary flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm", isOpen && "ring-2 ring-blue-300")}>
        <span className="truncate">
          {selected ? deviceLabel(selected) : value ? value : <span className="text-ds-text-muted">All devices on this account</span>}
        </span>
        <span className="text-ds-text-muted text-xs">▾</span>
      </button>

      {isOpen && <div className="border-ds-border-default bg-ds-surface-primary mt-1 max-h-72 w-full overflow-auto rounded-md border shadow-lg">
          <div className="border-ds-border-default border-b p-2">
            <Input autoFocus placeholder="Search devices…" value={query} onChange={e_1 => setQuery(e_1.target.value)} />
          </div>

          {loading && <div className="text-ds-text-muted p-3 text-sm">Loading…</div>}

          {!loading && <button type="button" onClick={() => {
        onChange("");
        setIsOpen(false);
        setQuery("");
      }} className={cn("hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm", !value ? "bg-blue-50 text-blue-700" : "text-ds-text-primary")}>
              <span className="min-w-0 flex-1 truncate">All devices on this account</span>
            </button>}

          {!loading && filtered.map(d_3 => <div key={d_3.id} className="hover:bg-ds-surface-hover flex items-center px-3">
                <button type="button" onClick={() => {
          onChange(d_3.deviceIden);
          if (d_3.connectorId) onConnectorSelect?.(d_3.connectorId);
          setIsOpen(false);
          setQuery("");
        }} className={cn("flex flex-1 items-center gap-2 py-2 text-left text-sm", d_3.deviceIden === value ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : "text-ds-text-primary")}>
                  <span className="min-w-0 flex-1 truncate">{deviceLabel(d_3)}</span>
                  {d_3.type && <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-700 shrink-0 rounded px-1.5 py-0.5 text-xs">
                      {d_3.type}
                    </span>}
                </button>
                <button type="button" onClick={() => handleRemove(d_3.id)} className="text-ds-text-muted pl-2 text-xs hover:text-red-500" title="Remove device">
                  ✕
                </button>
              </div>)}

          {!addMode && <button type="button" onClick={() => setAddMode(true)} className="hover:bg-ds-surface-hover border-ds-border-default text-ds-text-primary w-full border-t px-3 py-2 text-left text-sm">
              + Add a device by identifier
            </button>}

          {addMode && <form onSubmit={handleSaveDevice} className="border-ds-border-default space-y-2 border-t p-3">
              <Input placeholder="Device iden (from pushbullet.com)" value={newDeviceIden} onChange={e_2 => setNewDeviceIden(e_2.target.value)} autoFocus />
              <Input placeholder="Nickname (e.g. iPhone)" value={newNickname} onChange={e_3 => setNewNickname(e_3.target.value)} />
              {saveError && <div className="text-xs text-red-500">{saveError}</div>}
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={() => setAddMode(false)} className="text-ds-text-muted px-2 py-1 text-sm">
                  Cancel
                </button>
                <button type="submit" disabled={saving || !connectorId} className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50">
                  {saving ? "Saving…" : "Add"}
                </button>
              </div>
              {!connectorId && <p className="text-ds-text-muted text-xs">Pick a Pushbullet connector on the node first.</p>}
            </form>}
        </div>}
    </div>;
}